// Zustand des Aktenschranks (Handoff Dokumente §6.2). Die URL ist die Quelle: ?objekt, ?ordner, ?datei, ?q.
// Übernimmt die bewährte Logik des alten Aktenschranks: getrennte Tokens für Baum/Inhalt/Suche,
// Sitzungs-Cache je Ordner (stale-while-revalidate) und Sofort-Anzeige bekannter Unterordner aus dem Baum.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase/client';
import { alleSeiten } from '../supabase/alleSeiten';
import { objektName } from '../immobilien/objektKopf';
import { verwaltungsartVonObjekt } from '../immobilien/sidebarNav';
import {
  ORDNER_MIME, ladeOrdnerBaum, ladeOrdnerInhalt, sucheDateien,
  type BaumKnoten, type DriveEintrag,
} from './aktenschrankApi';
import { baumIndex, dateienJePfad, pfadSegmente, standardSuchBereich, suchOrdnerIds, suchWoerter, textAusschnitt, type Segment, type SuchBereich } from './aktenschrank';
import { istInterneAblage } from './kennzahlen';
import { DOKUMENTE_GEAENDERT } from './useDokumenteKopf';
import { POSTAUSGANG_OBJEKT_ID, POSTAUSGANG_ORDNER, ladePostausgangOrdnerId } from './postausgangOrdner';

export interface AktObjekt {
  id: string; nummer: string; name: string; ort: string; verwaltungsart: string | null; driveId: string | null;
  /** provision-objekt braucht objekte.verwaltungsart. */
  kannAnlegen: boolean;
  /** Verwaltungsobjekt: interne Unterlagen der Hausverwaltung (nur für Berechtigte sichtbar). */
  istVerwaltung?: boolean;
  /** Kein echtes Objekt: Drive-Ordner „Postausgang – Briefe ohne Objekt“ (nur Live-Inhalt, kein Baum). */
  pseudo?: boolean;
}

/** App-Wissen zu einer Drive-Datei (aus dokumente). */
export interface DateiMeta {
  id: string; gdrive_file_id: string; kategorie: string | null; quelle: string | null;
  ai_summary: string | null; ablage_pfad: string | null; titel: string | null;
  /** #607: für den Freigabe-Dialog (Vorschlag/Warnung bei persönlichen Dokumenten) */
  objekt_id?: string | null; mieter_id?: string | null; eigentuemer_id?: string | null; dokument_typ?: string | null; datei_name?: string | null;
}

export interface SuchTrefferAnzeige {
  id: string; name: string; mimeType: string | null;
  objekt: { id: string; nummer: string; name: string } | null;
  parentId: string | null; pfad: string | null;
  /** Ausschnitt, wenn der Treffer nur im Inhalt liegt. */
  ausschnitt: string | null; kategorie: string | null;
}

export type Ansicht = 'liste' | 'kacheln';

const MIN_SUCHE = 2;

export async function ladeObjekte(): Promise<AktObjekt[]> {
  const { data, error } = await supabase.from('objekte')
    .select('id, objektnummer, bezeichnung, strasse, hausnummer, stadt, verwaltungsart, objekttyp, gdrive_folder_id, ist_verwaltung')
    .order('objektnummer');
  if (error) throw new Error(error.message);
  const sortierer = new Intl.Collator('de', { numeric: true });
  return (data ?? []).map(o => ({
    id: o.id as string,
    nummer: ((o.objektnummer as string | null) ?? '').trim(),
    name: objektName(o),
    ort: ((o.stadt as string | null) ?? '').trim(),
    verwaltungsart: verwaltungsartVonObjekt(o),
    driveId: (o.gdrive_folder_id as string | null) ?? null,
    kannAnlegen: !!((o.verwaltungsart as string | null) ?? '').trim(),
    istVerwaltung: o.ist_verwaltung === true,
  // Verwaltungsobjekt (interne Unterlagen, nur für Berechtigte geladen) steht immer oben.
  })).sort((a, b) => Number(b.istVerwaltung) - Number(a.istVerwaltung) || sortierer.compare(a.nummer || '~', b.nummer || '~'));
}

async function ladeMeta(objektId: string): Promise<DateiMeta[]> {
  return alleSeiten<DateiMeta>((von, bis) => supabase.from('dokumente')
    .select('id, gdrive_file_id, kategorie, quelle, ai_summary, ablage_pfad, titel, objekt_id, mieter_id, eigentuemer_id, dokument_typ, datei_name')
    .eq('objekt_id', objektId).not('gdrive_file_id', 'is', null).eq('is_deleted', false).neq('status', 'Archiviert')
    .order('id').range(von, bis));
}

/**
 * Inhaltssuche über die Drive-Dokumente der App (Name, Titel, Kategorie, KI-Zusammenfassung, Textauszug).
 * Direkt auf dokumente (die alte RPC search_dokumente lief in den Statement-Timeout und ist entfernt,
 * Migration 2026-10-19-search-dokumente-entfernen.sql). Jedes Wort muss irgendwo vorkommen.
 */
async function sucheInhalt(q: string, bereich?: { objektId: string; pfad?: string | null } | null): Promise<SuchTrefferAnzeige[]> {
  const woerter = suchWoerter(q);
  if (woerter.length === 0) return [];
  let abfrage = supabase.from('dokumente')
    .select('id, gdrive_file_id, datei_name, titel, kategorie, ablage_pfad, datei_typ, ai_summary, extracted_text, objekt:objekt_id(id, objektnummer, bezeichnung)')
    .not('gdrive_file_id', 'is', null).eq('is_deleted', false).neq('status', 'Archiviert');
  // #558: Suchbereich Objekt bzw. Ordner (samt Unterordnern über das Pfad-Präfix)
  if (bereich) {
    abfrage = abfrage.eq('objekt_id', bereich.objektId);
    if (bereich.pfad) abfrage = abfrage.or(`ablage_pfad.eq.${JSON.stringify(bereich.pfad)},ablage_pfad.like.${JSON.stringify(`${bereich.pfad}/%`)}`);
  }
  for (const w of woerter) {
    abfrage = abfrage.or(['datei_name', 'titel', 'kategorie', 'ai_summary', 'extracted_text'].map(f => `${f}.ilike.%${w}%`).join(','));
  }
  const { data: doks, error: e2 } = await abfrage.order('created_at', { ascending: false }).limit(25);
  if (e2) throw new Error(e2.message);
  const liste = (doks ?? []) as unknown as {
    id: string; gdrive_file_id: string; datei_name: string | null; titel: string | null; kategorie: string | null;
    ablage_pfad: string | null; datei_typ: string | null; ai_summary: string | null; extracted_text: string | null;
    objekt: { id: string; objektnummer: string | null; bezeichnung: string | null } | null;
  }[];
  if (liste.length === 0) return [];
  const textNachId = new Map(liste.map(d => [d.id, d.extracted_text || d.ai_summary]));
  const { data: eltern } = await supabase.from('gdrive_ordner').select('gdrive_id, parent_gdrive_id').in('gdrive_id', liste.map(d => d.gdrive_file_id));
  const parentNach = new Map((eltern ?? []).map(e => [e.gdrive_id as string, e.parent_gdrive_id as string | null]));
  return liste.map(d => ({
    id: d.gdrive_file_id, name: d.datei_name || d.titel || 'Datei', mimeType: d.datei_typ,
    objekt: d.objekt ? { id: d.objekt.id, nummer: d.objekt.objektnummer ?? '', name: d.objekt.bezeichnung ?? '' } : null,
    parentId: parentNach.get(d.gdrive_file_id) ?? null, pfad: d.ablage_pfad,
    ausschnitt: textAusschnitt(textNachId.get(d.id), q) || null, kategorie: d.kategorie,
  }));
}

export function useAktenschrank() {
  const [params, setParams] = useSearchParams();
  const objektId = params.get('objekt');
  const ordnerParam = params.get('ordner');
  const dateiId = params.get('datei');
  const q = params.get('q') ?? '';
  const bereichParam = params.get('bereich');

  // ── Objekte ────────────────────────────────────────────────────────────────
  const [objekte, setObjekte] = useState<AktObjekt[] | null>(null);
  const [objekteFehler, setObjekteFehler] = useState<string | null>(null);
  const [objekteRunde, setObjekteRunde] = useState(0);
  useEffect(() => {
    let aktiv = true;
    Promise.all([ladeObjekte(), ladePostausgangOrdnerId().catch(() => null)]).then(([o, postId]) => {
      if (!aktiv) return;
      // Postausgang ohne Objekt direkt nach der Verwaltungsakte (erst sichtbar, wenn der Ordner einmal angelegt wurde)
      const pseudo: AktObjekt[] = postId ? [{ id: POSTAUSGANG_OBJEKT_ID, nummer: 'POST', name: POSTAUSGANG_ORDNER, ort: '', verwaltungsart: null, driveId: postId, kannAnlegen: false, pseudo: true }] : [];
      const v = o.filter(x => x.istVerwaltung);
      setObjekte([...v, ...pseudo, ...o.filter(x => !x.istVerwaltung)]); setObjekteFehler(null);
    })
      .catch((e: unknown) => { if (aktiv) setObjekteFehler(e instanceof Error ? e.message : 'Objekte nicht geladen'); });
    return () => { aktiv = false; };
  }, [objekteRunde]);
  const objekt = useMemo(() => objekte?.find(o => o.id === objektId) ?? null, [objekte, objektId]);
  const wurzelId = objekt?.driveId ?? null;
  const ordnerId = ordnerParam || wurzelId;

  // ── URL setzen (Tab bleibt erhalten) ──────────────────────────────────────
  const setze = useCallback((werte: Partial<Record<'objekt' | 'ordner' | 'datei' | 'q' | 'bereich', string | null>>, ersetzen = false) => {
    setParams(p => {
      const n = new URLSearchParams(p);
      for (const [k, v] of Object.entries(werte)) { if (v) n.set(k, v); else n.delete(k); }
      return n;
    }, { replace: ersetzen });
  }, [setParams]);

  // ── Baum + App-Wissen je Objekt ───────────────────────────────────────────
  const baumToken = useRef(0);
  const [baum, setBaum] = useState<BaumKnoten[]>([]);
  const [meta, setMeta] = useState<DateiMeta[]>([]);
  const [baumLaedt, setBaumLaedt] = useState(false);
  const [baumFehler, setBaumFehler] = useState<string | null>(null);
  const [baumRunde, setBaumRunde] = useState(0);
  const inhaltCache = useRef(new Map<string, DriveEintrag[]>());
  // Live gesehene Ordner (id → Name, Eltern) für den Breadcrumb von Ordnern außerhalb des Baums.
  const [bekannt, setBekannt] = useState<ReadonlyMap<string, { name: string; parent: string | null }>>(new Map());

  useEffect(() => {
    inhaltCache.current.clear();
    setBekannt(new Map());
    setBaum([]); setMeta([]); setBaumFehler(null);
  }, [objektId]);

  useEffect(() => {
    if (!objektId || !wurzelId) { setBaumLaedt(false); return; }
    if (objektId === POSTAUSGANG_OBJEKT_ID) { setBaum([]); setMeta([]); setBaumLaedt(false); return; }
    const token = ++baumToken.current;
    setBaumLaedt(true);
    Promise.all([ladeOrdnerBaum(objektId), ladeMeta(objektId)])
      .then(([b, m]) => { if (token === baumToken.current) { setBaum(b); setMeta(m); setBaumFehler(null); } })
      .catch((e: unknown) => { if (token === baumToken.current) setBaumFehler(e instanceof Error ? e.message : 'Ordnerbaum nicht geladen'); })
      .finally(() => { if (token === baumToken.current) setBaumLaedt(false); });
  }, [objektId, wurzelId, baumRunde]);

  const index = useMemo(() => baumIndex(baum), [baum]);
  const metaNachDatei = useMemo(() => new Map(meta.map(m => [m.gdrive_file_id, m])), [meta]);
  const objektPfade = useMemo(() => meta.map(m => m.ablage_pfad).filter(p => !istInterneAblage(p)), [meta]);
  const zaehler = useMemo(() => dateienJePfad(objektPfade), [objektPfade]);

  // ── Live-Inhalt des aktuellen Ordners ─────────────────────────────────────
  const ladeToken = useRef(0);
  const [inhalt, setInhalt] = useState<DriveEintrag[]>([]);
  const [inhaltLaedt, setInhaltLaedt] = useState(false);
  const [dateienLadenNoch, setDateienLadenNoch] = useState(false);
  const [inhaltFehler, setInhaltFehler] = useState<string | null>(null);
  const [inhaltRunde, setInhaltRunde] = useState(0);

  const bekannteUnterordner = useCallback((id: string): DriveEintrag[] => {
    const kinder = id === wurzelId ? baum : (index.nachId.get(id)?.kinder ?? []);
    return kinder.map(k => ({ id: k.gdriveId, name: k.name, mimeType: ORDNER_MIME }));
  }, [wurzelId, baum, index]);

  useEffect(() => {
    if (!ordnerId) { setInhalt([]); setInhaltLaedt(false); return; }
    const token = ++ladeToken.current;
    setInhaltFehler(null);
    const cached = inhaltCache.current.get(ordnerId);
    const vorab = cached ? [] : bekannteUnterordner(ordnerId);
    if (cached) { setInhalt(cached); setInhaltLaedt(false); setDateienLadenNoch(false); }
    else if (vorab.length) { setInhalt(vorab); setInhaltLaedt(false); setDateienLadenNoch(true); }
    else { setInhalt([]); setInhaltLaedt(true); setDateienLadenNoch(false); }
    ladeOrdnerInhalt(ordnerId)
      .then(e => {
        if (token !== ladeToken.current) return;
        inhaltCache.current.set(ordnerId, e);
        setInhalt(e);
        const neu = e.filter(x => x.mimeType === ORDNER_MIME);
        if (neu.length) setBekannt(alt => { const m = new Map(alt); for (const o of neu) m.set(o.id, { name: o.name, parent: ordnerId }); return m; });
      })
      .catch((e: unknown) => {
        if (token !== ladeToken.current) return;
        setInhaltFehler(e instanceof Error ? e.message : 'Ordner nicht geladen');
        if (!cached && vorab.length === 0) setInhalt([]);
      })
      .finally(() => { if (token === ladeToken.current) { setInhaltLaedt(false); setDateienLadenNoch(false); } });
    // bekannteUnterordner bewusst nicht als Abhängigkeit: ein nachgeladener Baum soll den Ordner nicht neu laden.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordnerId, inhaltRunde]);

  // ── Breadcrumb ────────────────────────────────────────────────────────────
  const segmente: Segment[] = useMemo(() => {
    if (!objekt || !wurzelId) return [];
    const wurzelName = [objekt.nummer, objekt.verwaltungsart].filter(Boolean).join(' ') || objekt.name;
    if (!ordnerId || ordnerId === wurzelId) return pfadSegmente(wurzelId, wurzelName, null, index.nachPfad);
    const knoten = index.nachId.get(ordnerId);
    if (knoten) return pfadSegmente(wurzelId, wurzelName, knoten, index.nachPfad);
    // Außerhalb des Baums: über live gesehene Eltern hochlaufen, bis ein Baum-Knoten oder die Wurzel erreicht ist.
    const kette: Segment[] = [];
    let id: string | null = ordnerId;
    for (let i = 0; id && i < 20; i++) {
      if (id === wurzelId) return [...pfadSegmente(wurzelId, wurzelName, null, index.nachPfad), ...kette];
      const k = index.nachId.get(id);
      if (k) return [...pfadSegmente(wurzelId, wurzelName, k, index.nachPfad), ...kette];
      const b = bekannt.get(id);
      kette.unshift({ id, name: b?.name ?? 'Ordner', pfad: '' });
      id = b?.parent ?? null;
    }
    return [...pfadSegmente(wurzelId, wurzelName, null, index.nachPfad), ...kette];
  }, [objekt, wurzelId, ordnerId, index, bekannt]);

  // ── Globale Suche (Name über Drive, Inhalt über die Drive-Dokumente der App) ──
  const suchToken = useRef(0);
  const [treffer, setTreffer] = useState<SuchTrefferAnzeige[]>([]);
  const [sucheLaedt, setSucheLaedt] = useState(false);
  const [sucheFehler, setSucheFehler] = useState<string | null>(null);
  // #558: Suchbereich — Voreinstellung nach Ort (Ordner → Ordner, Objekt → Objekt, sonst überall), per URL umschaltbar.
  const suchBereich: SuchBereich = bereichParam === 'alle' || bereichParam === 'objekt' || bereichParam === 'ordner'
    ? (bereichParam !== 'alle' && !objektId ? 'alle' : bereichParam as SuchBereich)
    : standardSuchBereich(objektId, ordnerId, wurzelId);
  const suchBereichOrdner = useMemo(() => {
    if (suchBereich === 'alle' || !wurzelId) return null;
    const start = suchBereich === 'ordner' && ordnerId ? ordnerId : wurzelId;
    return { ids: suchOrdnerIds(start, wurzelId, baum, index.nachId), pfad: start === wurzelId ? null : (index.nachId.get(start)?.pfad ?? null) };
  }, [suchBereich, wurzelId, ordnerId, baum, index]);
  useEffect(() => {
    const anfrage = q.trim();
    if (anfrage.length < MIN_SUCHE) { suchToken.current++; setTreffer([]); setSucheFehler(null); setSucheLaedt(false); return; }
    const timer = setTimeout(() => {
      const token = ++suchToken.current;
      setSucheLaedt(true); setSucheFehler(null);
      const inhaltBereich = suchBereichOrdner && objektId && objektId !== POSTAUSGANG_OBJEKT_ID ? { objektId, pfad: suchBereichOrdner.pfad } : null;
      Promise.allSettled([sucheDateien(anfrage, suchBereichOrdner?.ids), sucheInhalt(anfrage, inhaltBereich)]).then(([name, inhaltErg]) => {
        if (token !== suchToken.current) return;
        const liste: SuchTrefferAnzeige[] = [];
        const gesehen = new Set<string>();
        if (name.status === 'fulfilled') {
          for (const t of name.value) {
            gesehen.add(t.id);
            liste.push({
              id: t.id, name: t.name, mimeType: t.mimeType, parentId: t.parentId, pfad: t.pfad, ausschnitt: null, kategorie: null,
              objekt: t.objekt ? { id: t.objekt.id, nummer: t.objekt.objektnummer, name: t.objekt.bezeichnung } : null,
            });
          }
        }
        if (inhaltErg.status === 'fulfilled') for (const t of inhaltErg.value) if (!gesehen.has(t.id)) { gesehen.add(t.id); liste.push(t); }
        setTreffer(liste);
        if (name.status === 'rejected' && inhaltErg.status === 'rejected') setSucheFehler(name.reason instanceof Error ? name.reason.message : 'Suche fehlgeschlagen');
        setSucheLaedt(false);
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [q, suchBereichOrdner, objektId]);

  // ── Datei ohne Ordner (Deep-Link, „Zuletzt hinzugefügt“, Inhaltstreffer): Elternordner ermitteln ──
  // Erst über dokumente.ablage_pfad + Baum (sobald geladen), sonst über den gdrive_ordner-Cache.
  const baumBereit = !!objektId && !!wurzelId && !baumLaedt;
  useEffect(() => {
    if (!dateiId || ordnerParam) return;
    if (objektId && wurzelId && !baumBereit) return;
    const m = metaNachDatei.get(dateiId);
    const knoten = m?.ablage_pfad ? index.nachPfad.get(m.ablage_pfad.trim()) : undefined;
    if (knoten) { setze({ ordner: knoten.gdriveId }, true); return; }
    let aktiv = true;
    supabase.from('gdrive_ordner').select('parent_gdrive_id, objekt_id').eq('gdrive_id', dateiId).maybeSingle()
      .then(({ data }) => {
        if (!aktiv || !data?.parent_gdrive_id) return;
        setze({ ordner: data.parent_gdrive_id as string, ...(!objektId && data.objekt_id ? { objekt: data.objekt_id as string } : {}) }, true);
      });
    return () => { aktiv = false; };
  }, [dateiId, ordnerParam, objektId, wurzelId, baumBereit, metaNachDatei, index, setze]);

  // Änderungen von außen (Ablage, Signatur) → Baum/Zähler neu
  useEffect(() => {
    const neu = () => setBaumRunde(r => r + 1);
    window.addEventListener(DOKUMENTE_GEAENDERT, neu);
    return () => window.removeEventListener(DOKUMENTE_GEAENDERT, neu);
  }, []);

  // ── Aktionen ──────────────────────────────────────────────────────────────
  const waehleObjekt = useCallback((id: string, ersetzen = false) => setze({ objekt: id, ordner: null, datei: null }, ersetzen), [setze]);
  const oeffneOrdner = useCallback((id: string, name?: string) => {
    if (name && ordnerId) setBekannt(alt => (alt.has(id) ? alt : new Map(alt).set(id, { name, parent: ordnerId })));
    setze({ ordner: id === wurzelId ? null : id, datei: null });
  }, [setze, wurzelId, ordnerId]);
  const waehleDatei = useCallback((id: string | null) => setze({ datei: id }, true), [setze]);
  const setzeSuche = useCallback((text: string) => setze({ q: text || null }, true), [setze]);
  const setzeSuchBereich = useCallback((b: SuchBereich) => setze({ bereich: b }, true), [setze]);
  const oeffneTreffer = useCallback((t: SuchTrefferAnzeige) => {
    if (t.parentId) setBekannt(alt => (alt.has(t.parentId as string) || !t.pfad ? alt : new Map(alt).set(t.parentId as string, { name: t.pfad.split('/').pop() ?? 'Ordner', parent: null })));
    setze({ q: null, objekt: t.objekt?.id ?? objektId, ordner: t.parentId, datei: t.id });
  }, [setze, objektId]);
  /** Nach Upload/Umbenennen/Verschieben/Papierkorb: betroffene Ordner aus dem Cache werfen und neu laden. */
  const aktualisiere = useCallback((ordnerIds: readonly string[]) => {
    for (const id of ordnerIds) inhaltCache.current.delete(id);
    setInhaltRunde(r => r + 1);
    setBaumRunde(r => r + 1);
  }, []);
  const neuLadenObjekte = useCallback(() => setObjekteRunde(r => r + 1), []);

  return {
    objekte, objekteFehler, objekt, objektId, wurzelId, ordnerId, dateiId, q,
    baum, baumLaedt, baumFehler, index, zaehler, dateienGesamt: objektPfade.length, metaNachDatei,
    inhalt, inhaltLaedt, dateienLadenNoch, inhaltFehler, segmente,
    treffer, sucheLaedt, sucheFehler, sucheAktiv: q.trim().length >= MIN_SUCHE, suchBereich, setzeSuchBereich,
    waehleObjekt, oeffneOrdner, waehleDatei, setzeSuche, oeffneTreffer, aktualisiere, neuLadenObjekte,
    neuLadenInhalt: () => setInhaltRunde(r => r + 1),
  };
}

export type AktenschrankZustand = ReturnType<typeof useAktenschrank>;
